import { AnyAction } from '@reduxjs/toolkit';
import { noop } from 'lodash';
import React from 'react';
import useIsMounted from 'Hooks/useIsMounted';
import reducer, { initialState, PCIFileUploaderState } from './reducer';

const UploaderContext = React.createContext<PCIFileUploaderState>(initialState);
const UploaderDispatchContext = React.createContext<React.Dispatch<AnyAction>>(() => {});

function UploaderProvider({
    children,
    listProps
}: {
    children: React.ReactNode;
    listProps: {
        listName: string;
        listType: SingleListType;
        listId: number;
        merchantSpecific: boolean;
        customListType: CustomListObj | undefined;
    };
}) {
    const isMounted = useIsMounted();
    const [state, unsafeDispatch] = React.useReducer(reducer, { ...initialState, ...listProps });
    // don't dispatch anything if we're not mounted -- noop and it's a memory leak
    const dispatch = isMounted ? unsafeDispatch : noop;

    return (
        <UploaderContext.Provider value={state}>
            <UploaderDispatchContext.Provider value={dispatch}>{children}</UploaderDispatchContext.Provider>
        </UploaderContext.Provider>
    );
}

function useUploaderContext() {
    const context = React.useContext(UploaderContext);
    if (context === undefined) {
        throw new Error('useUploaderContext must be used within a UploaderProvider');
    }
    return context;
}

function useUploaderDispatchContext() {
    const context = React.useContext(UploaderDispatchContext);

    if (context === undefined) {
        throw new Error('useUploaderDispatchContext must be used within a UploaderDispatchContext');
    }
    return context;
}

export { UploaderProvider, useUploaderContext, useUploaderDispatchContext };
